import Vue from "vue"
import Vuex, { ActionTree, GetterTree, MutationTree } from "vuex"

import "@/scripts/array-operations"
import setBrandingLink from "@/scripts/brandingLink"

import { cases } from "./cases"
import { features } from "./features"
import { setup } from "./setup"

import { CasesNotification, NotificationType, Team, TimeZone, Field, TemplateDetails, Case } from "@/types"
import { CurrentLogin, LoginRequest, LoginResultTenant, LoginState } from "@/types/loginRequest"
import { IntegrationSummary, IntegrationUploadOptions } from "@/types/integration"
import { UserSettings } from "@/types/userSettings"
import { Subscription } from "@/types/subscription"

import api from "@/services/api"
import apiTenant from "@/services/api_Tenant"
import apiNotification from "@/services/api_Notification"
import integrationApi from "@/services/api_Integrations"
import apiRecordNameSettings from "@/services/api_RecordNameSettings"
import apiCaseNameSettings from "@/services/api_CaseNameSettings"
import subscriptionService from "@/services/api/subscriptionService"
import webfileApi from "@/services/api/api_webfiles"

import hubNotificationService from "@/services/hubNotificationService"

import { CurrentUser, SystemUser, UserPreferences, UserWithFields } from "@/types/user"
import { RawLocation, Route } from "vue-router"
import apiUserStatus from "@/services/api_UserStatus"

import { getStylesheetName, isNewStyle } from "@/utils/ThemeLoader"
import { convertToErrorInfo, loadErrorInfo } from "@/scripts/errorHandling"

import { UserStatus } from "@/types/userStatus"
import { DefaultValueObject, FirstDayOfWeekSetting, TenantSettingKeys } from "@/types/tenantSetting"
import ConnectionStatus from "@/types/connectionStatus"
import { TenantFeatures } from "@/types/tenantFeatures"
import { isEqual } from "lodash"
import { utils } from "@/mixins/utils"
import { DateTime, Settings } from "luxon"
import { ErrorInfo } from "@/types/error"
import { TimeManagementSettings } from "@/types/timeManagement"
import { DayOfWeek, ScheduleMessage } from "@/types/schedule"
import { TimeZoneAlias } from "@/types/timeZone"
import DataCache from "@/caching/caching"
import { CachedItemType } from "@/types/caching/cachedItemType"
import apiDefaultCache from "@/services/apiDefaultCache"
import { clearAllApiCaches } from "@/services/apiCache"
Vue.use(Vuex)

let apiErrorHide: NodeJS.Timeout | null = null

export interface CurrentRoute {
	name?: string | null,
	path: string,
	fullPath?: string,
	meta?: any
}

export interface StateRouter {
	currentRoute: CurrentRoute,
	push: (location: RawLocation) => void,
	resolve: (
		to: RawLocation,
		current?: Route,
		append?: boolean
	) => {
		route: Route,
	},
	go: (n: number) => void
}

export interface BaseState {
	router: StateRouter | null,
	screenWidth: number,
	uiVersion: string | null,
	me: CurrentUser | null
	userSettings: UserSettings | null
	tenantEnabledFeatures: { [key: string]: boolean }
	userPictureUrls: { [key: string]: string }
	subscription: Subscription | null
	teams: Team[]
	requestingUserPictureUrls: { [key: string]: Promise<string> }
	search: string
	darkMode: boolean
	browserNotificationsPermission: boolean
	notifications: CasesNotification[]
	caseName: string
	recordName: string
	redirectOnLogin: string | null
	integrations: IntegrationSummary[],
	tenantSettings: { [key: string]: any} | null,
	selectTenants: LoginResultTenant[] | null,
	manualLogout: boolean,
	loginState: LoginState,
	currentLogin: CurrentLogin | null,
	systemUser: SystemUser | null,
	userToast: string | null
	availableUserStatuses: UserStatus[]
	materialLoaded: boolean,
	notificationState: ConnectionStatus | null
	interfaceLoading: boolean,
	checkForUpdates: boolean,
	timeZones: TimeZone[],
	displayTimezone: TimeZone | null,
	scheduleToastMessage: ScheduleMessage | null
	timeManagementSettings: TimeManagementSettings | null,
	fileUploadOptions: IntegrationUploadOptions[] | null
	userInitComplete: boolean,
	userLoadError: ErrorInfo | null,
	apiError: ErrorInfo | null,

	/* --- Data Cache --- */
	fieldCache: DataCache<Field> | null,
	templateCache: DataCache<TemplateDetails> | null,
	userCache: DataCache<UserWithFields> | null,
	/* ------------------ */
}

export const state: BaseState = {
	router: null,
	screenWidth: 0,
	uiVersion: null,
	me: null,
	userSettings: null,
	tenantEnabledFeatures: {},
	userPictureUrls: {},
	subscription: null,
	teams: [],
	timeZones: [],
	requestingUserPictureUrls: {},
	search: "",
	darkMode: true,
	browserNotificationsPermission: false,
	notifications: [],
	caseName: "Case",
	recordName: "Record",
	redirectOnLogin: null,
	integrations: [],
	tenantSettings: null,
	selectTenants: null,
	manualLogout: false,
	loginState: LoginState.NotLoggedIn,
	currentLogin: null,
	systemUser: null,
	userToast: null,
	availableUserStatuses: [],
	materialLoaded: false,
	notificationState: null,
	interfaceLoading: false,
	checkForUpdates: false,
	displayTimezone: null,
	timeManagementSettings: null,
	fileUploadOptions: null,
	scheduleToastMessage: null,
	userInitComplete: false,
	userLoadError: null,
	apiError: null,

	/* --- Data Cache --- */
	userCache: new DataCache<UserWithFields>(CachedItemType.User, () => api.usersGet()),
	fieldCache: new DataCache<Field>(CachedItemType.Field, () => api.fields()),
	templateCache: new DataCache<TemplateDetails>(CachedItemType.Template, () => api.templatesWithFields())
	/* ------------------ */
}

export const getters = {
	getCurrentRoute: (state: BaseState) => state.router ? state.router.currentRoute as CurrentRoute : null,
	getMe: (state: BaseState) => state.me,
	getSubscription: (state: BaseState) => state.subscription,
	hasSubscription: (state: BaseState) => !!(state.subscription && state.subscription.id > 0),
	getUserSettings: (state: BaseState) => state.userSettings,
	getScreenWidth: (state: BaseState) => state.screenWidth,
	getMyId: (state: BaseState) => state.me ? state.me.id : null,
	getUserPictureUrl: (state: BaseState) => (userId: string) => state.userPictureUrls[userId],
	getTeams: (state: BaseState) => state.teams,
	getTeamsById: (state: BaseState) => state.teams.toDictionary("id"),
	getSearch: (state: BaseState) => state.search,
	getBrowserNotificationsPermission: (state: BaseState) => state.browserNotificationsPermission,
	getNotifications: (state: BaseState) => state.notifications,
	getHighestUrgency: (state: BaseState) => state.notifications.length > 0 ? state.notifications[0].urgency : null,
	getCaseName: (state: BaseState) => state.me && state.me.caseName ? state.me.caseName : state.caseName,
	getRecordName: (state: BaseState) => state.me && state.me.recordName ? state.me.recordName : state.recordName,
	getPluralizedRecordName: (state: BaseState, getters: any) => utils.methods.pluralize(getters.getRecordName),
	getNomenclatureForRecord: (state: BaseState, getters: any) => (record: Case) => record.isCase ? getters.getCaseName : getters.getRecordName,
	getRecordOrCaseName: (state: BaseState, getters: any) => {
		const recordName = getters.getRecordName
		const caseName = getters.getCaseName

		if (state.tenantSettings?.allTemplatesAlwaysPromoted) {
			return caseName
		}

		return recordName.toLowerCase() === caseName.toLowerCase()
			? recordName
			: `${recordName}/${caseName}`
	},
	getHasIntegrations: (state: BaseState) => state.integrations.length > 0,
	getTenantSettings: (state: BaseState) => state.tenantSettings,
	getAvailableUserStatuses: (state: BaseState) => state.availableUserStatuses,
	getUseScalingComponent: (state: BaseState) => {
		if (state.tenantSettings && "checkTenantSize" in state.tenantSettings && !!state.tenantSettings["checkTenantSize"]) {
			const checkTenantSize = state.tenantSettings["checkTenantSize"] as DefaultValueObject
			if (checkTenantSize.isEnabled) {
				return (state.me && state.me.tenantSize && state.me.tenantSize > 500)
			}
		}
		return false
	},
	getIsSuiteEnabled: (state: BaseState) => {
		return state.me && state.me.tenantEnabledFeatures && state.me.tenantEnabledFeatures.includes(TenantFeatures.Suite)
	},
	getCurrentRouteName: (state: BaseState) => {
		return state.router ? state.router.currentRoute.name : null
	},
	getCurrentRouteMeta: (state: BaseState) => {
		const router = state.router
		return router ? router.currentRoute.meta : null
	},
	getUseV2: (state: BaseState, getters: { getCurrentRoute: CurrentRoute }) => {
		// Reference these first to ensure reactivity gets triggers on route change
		const userError = state.userLoadError
		const currentUser = state.me
		const systemUser = state.systemUser

		const route = getters?.getCurrentRoute
		const meta = route?.meta
		if (meta?.v1Only) {
			return false
		}

		if (systemUser || userError) {
			return true
		}

		if (!currentUser?.tenantEnabledFeatures?.includes(TenantFeatures.EnableV2)) {
			return false
		}
		if (currentUser?.preferences?.disableV2) {
			return false
		}
		if (currentUser?.tenantEnabledFeatures?.includes(TenantFeatures.NewUIOptIn)) {
			if (currentUser?.preferences?.enableV2) {
				return true
			} else {
				return false
			}
		}
		return true
	},
	getDarkMode: (state: BaseState) => {
		const router = state.router
		if (router && router.currentRoute.meta?.forceTheme !== undefined) {
			return router.currentRoute.meta?.forceTheme
		}

		return state.darkMode
	},
	getStylesheet: (state: BaseState, getters: any) => {
		const V2 = getters.getUseV2
		const darkMode = getters.getDarkMode
		return getStylesheetName(darkMode, V2)
	},
	getIsFeatureEnabled: (state: BaseState) => (feature: TenantFeatures) => !!state.tenantEnabledFeatures[feature],
	getCheckForUpdates: (state: BaseState) => state.checkForUpdates,
	getTenantTimezones: (state: BaseState) => (state.tenantSettings && state.tenantSettings["timeZones"] && Array.isArray(state.tenantSettings["timeZones"]) && state.tenantSettings["timeZones"].length > 0)
		? state.timeZones.filter(tz => state.tenantSettings!["timeZones"].includes(tz.id))
		: state.timeZones,
	getIsUserInitComplete: (state: BaseState) => state.userInitComplete,
	getDefaultFirstDayOfWeek (state: BaseState): DayOfWeek {
		const tenantSettings = state.tenantSettings
		if (tenantSettings && tenantSettings[TenantSettingKeys.DefaultFirstDayOfWeek]) {
			const firstDayOfWeekSettings = tenantSettings[TenantSettingKeys.DefaultFirstDayOfWeek] as FirstDayOfWeekSetting
			if (!firstDayOfWeekSettings.enabled) {
				return DayOfWeek.Monday
			} else {
				return firstDayOfWeekSettings.dayOfWeekSelected
			}
		} else {
			return DayOfWeek.Monday
		}
	}
}

export const actions: ActionTree<BaseState, BaseState> = {
	async startUp ({ state, commit, dispatch }) {
		try {
			// Browser notifications permission
			const storageItemResult: string | null = localStorage.getItem("browserNotificationsPermission")
			if (storageItemResult && storageItemResult === "true") {
				state.browserNotificationsPermission = true
				if (!(Notification.permission === "granted")) {
					localStorage.setItem("browserNotificationsPermission", "false")
					state.browserNotificationsPermission = false
				}
			} else {
				state.browserNotificationsPermission = false
			}

			dispatch("fetchTimeZones")

			// Get the UI version Number
			await fetch("manifest.json") // Relative path to the manifest.json file
				.then(response => response.json())
				.then(data => {
					const fullVersion: string = data.version
					const versionMatch = fullVersion?.match(/\d+\.\d+\.(\d+)\.\d+/)
					if (versionMatch && versionMatch[1]) {
						state.uiVersion = versionMatch[1]
					}
				})
				.catch(error => {
					console.log("Error fetching manifest.json:", error)
				})

			// TODO this should be a router guard rather than putting the router in the store
			// See guards.ts, we can use the vuex store inside a router guard
			if (state.router && (!state.router.currentRoute.meta || !state.router.currentRoute.meta.allowAnon)) {
				dispatch("userInit").then(() => {
					if (state.loginState === LoginState.Introduction) {
						if (!state.router) return

						const currentRoute = state.router.currentRoute
						if (currentRoute.name !== "login") {
							// Go to login page to do introduction - ensure that after login, the user comes back here
							commit("setLoginRedirect", currentRoute.path)
							state.router.push({ name: "login" })
						}
					}
				}).catch((error) => {
					console.log(error)
					if (!state.router) return
					const currentRoute = state.router.currentRoute
					if (currentRoute.name !== "login") {
						loadErrorInfo(error).then(loadedError => {
							commit("setUserLoadError", loadedError)
						})
					}
				})
			}
		} catch (err) {
			console.error(err)
			await dispatch("setMe", null)
			commit("setUserSettings", null)

			commit("setNotificationState", {
				connected: false,
				ableToReconnect: false
			})
		}
	},

	async refreshUserInfo ({ commit, dispatch, state }) {
		try {
			let user = await apiDefaultCache.me({ forceRefetch: true })
			if (user.tenantId) {
				await dispatch("setMe", user)
			} else {
				commit("setSystemUser", user)
				dispatch("setTheme", true)
			}
			commit("setUserLoadError", null)
		} catch (err) {
			console.error(err)
			throw err
		}
	},

	async logIn ({ state, commit, dispatch }, loginDetails: LoginRequest) {
		const loginResult = await api.login(loginDetails)

		if (!loginResult) {
			return
		}

		commit("setCurrentLogin", {
			allowSystemAdmin: loginResult.systemAdmin || false,
			requiresMfaSetup: loginResult.requiresMfaSetup || false,
			supportAccess: loginResult.supportAccess,
			requiresIntroduction: loginResult.requiresIntroduction
		})

		if (loginResult.loginTenants) {
			commit("setSelectTenants", loginResult.loginTenants)
			commit("setLoginState", LoginState.PendingTenantSelect)
			return
		} else if (loginResult.requiresMfa) {
			commit("setLoginState", LoginState.PendingMfa)
			return
		}

		dispatch("finishLogin")
	},

	async finishLogin ({ state, dispatch, commit }) {
		dispatch("userInit", {
			loginState: state.currentLogin && state.currentLogin.requiresIntroduction
				? LoginState.Introduction
				: LoginState.LoggedIn,
			redirect: true })
	},

	async finishIntroduction ({ state, dispatch, commit }) {
		commit("setCurrentLogin", {
			...state.currentLogin,
			requiresIntroduction: false
		})
		dispatch("loginRedirect")
	},

	async loginRedirect ({ state, dispatch, commit }) {
		let redirectRoute = "/dashboard"
		if (!state.router) return

		if (state.router.currentRoute.name === "dashboard") {
			// Navigate to login which will then take us to the dashboard
			redirectRoute = "/login"
		}
		state.router.push({ path: (state.redirectOnLogin ? state.redirectOnLogin : redirectRoute) })
		commit("setLoginRedirect", null)
	},

	async selectTenant ({ state, commit, dispatch }, { id, userId }: LoginResultTenant) {
		const result = await apiTenant.selectTenant(id, userId)
		commit("setSelectTenants", null)

		await dispatch("clearDataCaches")
		commit("setIntegrations", [])

		if (state.currentLogin) {
			// Set requires intro on current login if needed
			commit("setCurrentLogin", {
				...state.currentLogin,
				requiresIntroduction: result.requiresIntroduction
			})
		}

		if (result.requiresMfa) {
			commit("setLoginState", LoginState.PendingMfa)

			if (state.router && state.router.currentRoute.name !== "home") {
				state.router.push({ name: "home" })
			}
		} else {
			await dispatch("userInit", {
				loginState: result.requiresIntroduction ? LoginState.Introduction : LoginState.LoggedIn,
				redirect: true
			})

			if (state.loginState === LoginState.Introduction) {
				if (state.router && state.router.currentRoute.name !== "login") {
					// Send to login page to do introduction
					state.router.push({ name: "login" })
				}
				return
			}

			commit("setLoginState", LoginState.LoggedIn)
		}
	},

	async userInit ({ state, dispatch, commit },
		{ loginState, redirect }: {loginState: LoginState, redirect: boolean} = { loginState: LoginState.LoggedIn, redirect: false }) {
		commit("setUserInitComplete", false)
		commit("setLoginState", LoginState.Waiting)
		let user: CurrentUser | null

		try {
			user = await api.me()
		} catch (err) {
			commit("setLoginState", LoginState.NotLoggedIn)
			throw err
		}

		const introductionEnabled = user.tenantEnabledFeatures && user.tenantEnabledFeatures.includes(TenantFeatures.FirstTimeLogin)
		if (loginState === LoginState.Introduction) {
			if (!introductionEnabled) {
				// If we're not going to do the introduction, skip straight to logged in
				loginState = LoginState.LoggedIn
			}
		} else if (!user.hasDoneFirstLogin && introductionEnabled) {
			// Go to introduction. This state can be hit if the user logs in via SAML
			loginState = LoginState.Introduction
		}

		if (!user.tenantId) {
			// System user
			await dispatch("connectNotifications")
			commit("setSystemUser", user)
			commit("setLoginState", loginState)

			dispatch("setTheme", true)

			return
		}

		commit("setLoginState", loginState)

		if (redirect) {
			if (loginState !== LoginState.Introduction) {
				dispatch("loginRedirect")
			}
		}

		commit("setSystemUser", null)
		await dispatch("setMe", user)
		const loadSubscriptionPromise = dispatch("loadSubscription")
		commit("setUserSettings", user.settings)

		dispatch("setCheckForUpdates", true)

		const loadTeamsPromise = api.teamsGet()
			.catch(console.error)
			.then(teams => commit("setTeams", teams))

		const connectNotificationsPromise = dispatch("connectNotifications")
		const loadTenantSettingsPromise = dispatch("loadTenantSettings")

		let recordNamePromise: Promise<void> | null = null
		let caseNamePromise: Promise<void> | null = null
		if (!state.me!.recordName) {
			recordNamePromise = dispatch("getRecordName")
		}
		if (!state.me!.caseName) {
			caseNamePromise = dispatch("getCaseName")
		}

		if (state.me && state.me.isAdmin) {
			integrationApi.integrationsGetActive().then(integrations =>
				commit("setIntegrations", integrations))
		}

		const userStatusesPromise = dispatch("loadAvailableUserStatuses")

		const timeManagementSettingsPromise = api.timeManagementSettings()
			.then(timeManagementSettings => commit("setTimeManagementSettings", timeManagementSettings))

		const initializeTimeZonesPromise = state.timeZones && state.timeZones.length > 0
			? dispatch("initializeUiTimeZones", { user })
			: dispatch("fetchTimeZones").then(() => dispatch("initializeUiTimeZones", { user }))

		await Promise.allSettled([loadSubscriptionPromise, loadTeamsPromise, connectNotificationsPromise,
			loadTenantSettingsPromise, userStatusesPromise, recordNamePromise, caseNamePromise,
			timeManagementSettingsPromise, initializeTimeZonesPromise])

		commit("setUserInitComplete", true)
	},

	async logOut ({ commit, state, dispatch }, redirectBackOnLogin: boolean = false) {
		hubNotificationService.closeConnection()

		console.log("Logging out")
		await dispatch("setMe", null)
		commit("setLoginState", LoginState.NotLoggedIn)
		commit("setSubscription", null)
		commit("setUserSettings", null)
		commit("setIntegrations", [])
		commit("setTenantSettings", null)
		commit("setSelectTenants", null)
		commit("setCurrentLogin", null)
		commit("setSystemUser", null)
		commit("setAvailableUserStatuses", [])
		commit("setTimezones", null)
		clearAllApiCaches()

		dispatch("cases/detailsUnload")

		dispatch("setCheckForUpdates", false)

		const redirect = state.router && (state.router.currentRoute.name !== "login" && !(state.router.currentRoute.meta && state.router.currentRoute.meta.allowAnon))
		if (redirect) {
			commit("setLoginRedirect", redirectBackOnLogin ? state.router!.currentRoute.path : null)
		}

		(state as any).cases.viewsService.reset()

		if (redirect) {
			await state.router!.push({ name: "login" })
		}
	},

	async connectNotifications ({ commit, state, dispatch }) {
		if (hubNotificationService) {
			hubNotificationService.closeConnection()
		}

		// Get existing user notification
		apiNotification.axios = api.getAxios()
		commit("setNotifications", await apiNotification.notificationList())

		// Open notifications connection
		hubNotificationService.notLoggedIn = () => dispatch("logOut")
		hubNotificationService.connectionStateChanged = (newConnectionState: boolean) => commit("setNotificationState", {
			connected: newConnectionState,
			ableToReconnect: true
		})

		const connection = await hubNotificationService.openConnection()
		if (connection) {
			connection.on("notifications", (incomingNotifications: CasesNotification[]) => {
				const newNotifications: CasesNotification[] = []
				incomingNotifications.forEach(incomingNotification => {
					if (state.notifications!.some(n => n.id === incomingNotification.id) === false) {
						newNotifications.push(incomingNotification)
					}
				})

				if (newNotifications.length > 0) {
					const existingNotifications: CasesNotification[] = state.notifications
					commit("setNotifications", [
						...newNotifications,
						...existingNotifications
					])

					// Browser notifications
					if (state.browserNotificationsPermission && Notification.permission === "granted") {
						newNotifications.reverse().forEach(async n => {
							// Browser notifications can't use rich text, so remove any html from the message
							var messageBody = n.message.replace(/<[^>]*>?/gm, " ")

							const options: NotificationOptions = {
								body: messageBody,
								data: { casesNotification: n },
								icon: "images/icons/sv mark color 48px.png"
							}

							let notification = new Notification(n.title, options)
							notification.onclick = async function (_event) {
								// DS
								await apiNotification.notificationDelete(notification.data.casesNotification)

								// Store
								state.notifications.forEach((n, index, array) => {
									if (n.id === notification.data.casesNotification.id) {
										array.splice(index, 1)
									}
								})

								commit("setNotifications", state.notifications)

								// Route
								if (notification.data.casesNotification.type === NotificationType.RecordAssignedToUser ||
									notification.data.casesNotification.type === NotificationType.RecordUpdatedByAnotherUser ||
									notification.data.casesNotification.type === NotificationType.RuleAction) {
									const newPath: string = `/cases/${notification.data.casesNotification.data}`
									if (state.router && state.router.currentRoute.path !== newPath) {
										state.router.push({ path: newPath })
									}
								}
							}
						})
					}
				}
			})
			connection.on("refresh", async (refreshItemType: CachedItemType) => {
				switch (refreshItemType) {
					case CachedItemType.Template:
						commit("cases/addPendingRefresh", CachedItemType.Template)
						setDataCachePendingRefresh(state, CachedItemType.Template)
						break
					default:
						// Users, Fields
						setDataCachePendingRefresh(state, refreshItemType)
						break
				}
			})
			connection.on("supportexpiresoon", () => {
				commit("setUserToast", "Support access will expire soon")
			})
			connection.on("userupdated", async () => {
				console.log("User updated - refreshing ... ")
				await dispatch("setMe", await api.me())
			})
			connection.on("scheduleForecastComplete", async (message: string) => {
				if (state.scheduleToastMessage && state.scheduleToastMessage.scheduleId === message) {
					const toastTitle = (/schedule/i).test(message) ? message : `${message} Schedule`
					commit("setScheduleToastMessage", {
						scheduleId: message,
						message: `Forecasting Complete for ${toastTitle}`,
						variant: "success",
						autoClose: false
					})
				}
			})
			connection.on("logout", () => {
				dispatch("logOut")
			})
		}
	},

	async clearUserNotifications ({ commit }) {
		await api.clearUserNotifications()
		commit("setNotifications", [])
	},

	async markLocalNotificationsAsSent ({ commit, state }) {
		state.notifications.forEach(notification => {
			if (!notification?.sent) {
				notification.sent = true
				notification.sentDateTime = new Date(Date.now())
			}
		})

		commit("setNotifications", state.notifications)
	},

	async loadSubscription ({ commit, state }) {
		if (state.me && state.me.billingEnabled) {
			commit("setSubscription", await subscriptionService.getSubscriptionAsync())
		}
	},

	async subscriptionInvalid () {
		if (!state.router) return
		state.router.push({ name: "organization" })
	},

	async loadTeams ({ commit }) {
		commit("setTeams", await api.teamsGet())
	},

	async loadUserPictureUrl ({ state }, userId: string) {
		if (!state.userPictureUrls[userId]) {
			if (state.requestingUserPictureUrls[userId] != null) {
				return state.requestingUserPictureUrls[userId]
			}
			return state.requestingUserPictureUrls[userId] = new Promise(async (resolve, reject) => {
				const userDetails = state.userCache?.getById(userId) ?? await apiDefaultCache.userDetails(userId)
				if (!userDetails) return resolve(state.userPictureUrls[userId] = "images/blank_user.png")

				if (userDetails.pictureUrl) {
					return resolve(state.userPictureUrls[userId] = userDetails.pictureUrl!)
				}

				return resolve(state.userPictureUrls[userId] = api.pictureUrl(userId))
			})
		} else {
			return state.userPictureUrls[userId]
		}
	},

	async requestBrowserNotificationsPermission ({ commit }) {
		Notification.requestPermission().then(function (permission) {
			if (permission === "granted") {
				localStorage.setItem("browserNotificationsPermission", "true")
				commit("setBrowserNotificationsPermission", true)
			} else if (permission === "denied") {
				localStorage.setItem("browserNotificationsPermission", "false")
				commit("setBrowserNotificationsPermission", false)
			}
		})
	},

	async processNotification ({ commit }, notification: CasesNotification) {
		// DS
		await apiNotification.notificationDelete(notification)

		// Store
		state.notifications.forEach((n, index, array) => {
			if (n.id === notification.id) {
				array.splice(index, 1)
			}
		})

		commit("setNotifications", state.notifications)

		// Route
		if (notification.type === NotificationType.RecordAssignedToUser ||
			notification.type === NotificationType.RecordUpdatedByAnotherUser ||
			notification.type === NotificationType.RuleAction) {
			const newPath: string = `/cases/${notification.data}`
			if (state.router && state.router.currentRoute.path !== newPath) {
				state.router.push({ path: newPath })
			}
		}
	},

	async setTheme ({ commit, dispatch }, darkModeTheme) {
		commit("setDarkMode", darkModeTheme)
	},

	async resetBrandingLink () {
		setBrandingLink(null)
	},

	async getRecordName ({ commit }) {
		let recordNameSettings = await apiRecordNameSettings.recordNameSettingsList()
		commit("setRecordName", recordNameSettings ? recordNameSettings.firstOrDefault(s => s.default).title : "Record")
	},

	async getCaseName ({ commit }) {
		let caseNameSettings = await apiCaseNameSettings.caseNameSettingsList()
		commit("setCaseName", caseNameSettings ? caseNameSettings.firstOrDefault(s => s.default).title : "Case")
	},

	async loadTenantSettings ({ commit }) {
		const tenantSettings = await apiTenant.getTenantSettings()
		commit("setTenantSettings", tenantSettings ? tenantSettings.toDictionaryMapValue(sv => sv.setting.key, sv => sv.value) : {})
	},

	async loadTenantSettingsIfUnloaded ({ commit, state, dispatch }) {
		if (state.tenantSettings && Object.keys(state.tenantSettings).length > 0) {
			return
		}

		await dispatch("loadTenantSettings")
	},

	async loadAvailableUserStatuses ({ commit, state }) {
		const availableStatuses = await apiUserStatus.userStatusList()
		commit("setAvailableUserStatuses", availableStatuses)
	},

	async setCurrentUserStatus ({ commit, state }, newUserStatus: UserStatus) {
		if (!state.me) {
			return
		}
		await api.setUserStatus(state.me.id, newUserStatus.id!)
		commit("setCurrentUserStatus", newUserStatus)
	},

	async clearCurrentUserStatus ({ commit, state }) {
		if (!state.me) {
			return
		}
		await api.clearUserStatus(state.me.id)
		commit("clearCurrentUserStatus")
	},
	async updateUserPreferences ({ commit, state, dispatch }, userPreferences: UserPreferences) {
		if (!userPreferences || isEqual(userPreferences, (state.me) ? state.me.preferences : null)) {
			return
		}
		const successful = (await api.updateUserPreferences(userPreferences))
		if (successful) {
			commit("setUserPreferences", userPreferences)
		}
	},
	/**
	 * Updates the currently logged-in user
	 * Updates their theme, allowing the new interface to be loaded when the user logs in & cleared when they log out
	 * @param me The user to update
	 */
	async setMe ({ commit, dispatch, state }, me: CurrentUser | null) {
		commit("setMe", me)
		let darkMode = state.darkMode
		if (me && me.settings && me.settings.settings && me.settings.settings["darkMode"] != null) {
			darkMode = !!me.settings.settings["darkMode"]
		}

		await dispatch("setTheme", darkMode)
	},
	async setCheckForUpdates ({ commit, state }, checkForUpdates: boolean) {
		commit("setCheckForUpdates", checkForUpdates)
	},

	async setDisplayTimezone ({ commit }, timezone: TimeZone | null) {
		commit("setDisplayTimezone", timezone)
		api.setSessionValue("displayTimezone", timezone?.id || null)
	},

	async onApiError ({ commit }) {
		if (apiErrorHide) {
			clearTimeout(apiErrorHide)
			apiErrorHide = null
		}

		commit("setApiError", await loadErrorInfo(null))
		apiErrorHide = setTimeout(() => {
			commit("setApiError", null)
		}, 30000)
	},

	async loadFileUploadOptions ({ commit, state }) {
		if (state.fileUploadOptions === null) {
			commit("setFileUploadOptions", await integrationApi.integrationUploadOptions())
		}
	},
	async initDataCaches ({ state }) {
		console.log("Initializing data caches")
		state.userCache = state.userCache ?? new DataCache<UserWithFields>(CachedItemType.User, () => api.usersGet())
		state.fieldCache = state.fieldCache ?? new DataCache<Field>(CachedItemType.Field, () => api.fields())
		state.templateCache = state.templateCache ?? new DataCache<TemplateDetails>(CachedItemType.Template, () => api.templatesWithFields(), undefined, true)
	},
	async clearDataCaches ({ state, dispatch }) {
		console.log("Clearing data caches")
		state.userCache = null
		state.fieldCache = null
		state.templateCache = null
		await dispatch("initDataCaches")
	},
	async initializeUiTimeZones ({ state, commit }, { user }: { user: CurrentUser | null }) {
		const timeZones = state.timeZones ?? []
		const timezoneAliasesFeatureEnabled = user &&
			user.tenantEnabledFeatures && user.tenantEnabledFeatures.includes(TenantFeatures.TimezoneAliases)

		const timezoneAliasesSettingExists =
			state.tenantSettings && TenantSettingKeys.TimezoneAliases in state.tenantSettings

		if (timezoneAliasesFeatureEnabled && timezoneAliasesSettingExists) {
			const aliasedTimezones = timeZones.map(tz => {
				const timezoneAliasSettings =
					state.tenantSettings![TenantSettingKeys.TimezoneAliases] as TimeZoneAlias[]
				const tzAlias = timezoneAliasSettings.firstOrDefault(tzAlias => tzAlias.timezone === tz.id)
				return {
					...tz,
					nonAliasedTitle: tz.title,
					title: tzAlias ? tzAlias.alias : tz.title
				} as TimeZone
			})

			commit("setTimezones", aliasedTimezones)
			if (state.displayTimezone) {
				commit("setDisplayTimezone", aliasedTimezones.firstOrDefault(tz => tz.id === state.displayTimezone!.id))
			}
		} else {
			commit("setTimezones", timeZones.map(tz => {
				return {
					...tz,
					nonAliasedTitle: tz.title
				} as TimeZone
			}))
		}
	},
	async fetchTimeZones ({ commit }) {
		commit("setTimezones", await api.timeZonesGet())
	}
}

export const mutations: MutationTree<BaseState> = {
	setRouter: (state: BaseState, router: StateRouter) => {
		state.router = router
	},
	setMe: (state: BaseState, myDetails: CurrentUser) => {
		state.me = myDetails
		if (myDetails && myDetails.tenantEnabledFeatures) {
			state.tenantEnabledFeatures = myDetails && myDetails.tenantEnabledFeatures
				? myDetails.tenantEnabledFeatures!.toDictionaryMapValue(f => f, f => true)
				: {}
		}
		setBrandingLink(myDetails ? myDetails.tenantId : null)

		if (!myDetails) {
			// User is logging out
			(Settings as any).defaultZone = "system"
		} else if (state.tenantEnabledFeatures[TenantFeatures.UiTimezone] &&
			state.timeZones &&
			myDetails.settings?.settings &&
			myDetails.settings.settings["showTimezoneSelect"] &&
			myDetails.sessionValues &&
			myDetails.sessionValues["displayTimezone"]) {
			const timeZoneId = myDetails.sessionValues!["displayTimezone"]
			const timezone = state.timeZones.find(t => t.id === timeZoneId!);

			(Settings as any).defaultZone = timezone ? timezone.iana : "system"
			state.displayTimezone = timezone || null
		}
	},
	setFeatures: (state: BaseState, features: { [key: string]: boolean }) => {
		state.tenantEnabledFeatures = features
	},
	setSubscription: (state: BaseState, subscription: Subscription) => state.subscription = subscription,
	setUserSettings: (state: BaseState, userSettings: UserSettings) => {
		state.userSettings = userSettings
	},
	setAvailableUserStatuses: (state: BaseState, userStatuses: UserStatus[]) => {
		state.availableUserStatuses = userStatuses
	},
	setCurrentUserStatus: (state: BaseState, userStatus: UserStatus) => {
		if (state.me) {
			state.me.currentStatus = userStatus
		}
	},
	clearCurrentUserStatus: (state: BaseState) => {
		if (state.me) {
			state.me.currentStatus = undefined
		}
	},
	setScreenWidth: (state: BaseState, width: number) => state.screenWidth = width,
	setTeams: (state: BaseState, teams: Team[]) => state.teams = teams,
	setSearch: (state: BaseState, search: string) => state.search = search,
	setDarkMode: (state: BaseState, darkMode: boolean) => state.darkMode = darkMode,
	setBrowserNotificationsPermission: (state: BaseState, browserNotificationsPermission: boolean) => state.browserNotificationsPermission = browserNotificationsPermission,
	setNotifications: (state: BaseState, notifications: CasesNotification[]) => state.notifications = notifications
		? notifications.sortBy("urgency", true)
		: [],
	setCaseName: (state: BaseState, caseName: string) => state.caseName = caseName,
	setRecordName: (state: BaseState, recordName: string) => state.recordName = recordName,
	setLoginRedirect: (state: BaseState, routeName: string | null) => state.redirectOnLogin = routeName,
	setIntegrations: (state: BaseState, integrations: IntegrationSummary[]) => state.integrations = integrations,
	setTenantSettings: (state: BaseState, tenantSettings: { [key: string]: any }) => state.tenantSettings = tenantSettings,
	setSelectTenants: (state: BaseState, selectTenants: LoginResultTenant[] | null) => state.selectTenants = selectTenants,
	setManualLogout: (state: BaseState, manualLogout: boolean) => state.manualLogout = manualLogout,
	setLoginState: (state: BaseState, loginState: LoginState) => state.loginState = loginState,
	setCurrentLogin: (state: BaseState, currentLogin: CurrentLogin | null) => state.currentLogin = currentLogin,
	setSystemUser: (state: BaseState, systemUser: SystemUser) => state.systemUser = systemUser,
	setUserToast: (state: BaseState, message: string | null) => state.userToast = message,
	setMaterialLoaded: (state: BaseState, loaded: boolean) => state.materialLoaded = loaded,
	setNotificationState: (state: BaseState, connectionState: ConnectionStatus | null) => state.notificationState = connectionState,
	setUserPreferences: (state: BaseState, userPreferences: UserPreferences) => {
		if (!state.me) {
			return
		}
		state.me.preferences = userPreferences
	},
	setInterfaceLoading: (state: BaseState, loading: boolean) => state.interfaceLoading = loading,
	setCheckForUpdates: (state: BaseState, checkForUpdates: boolean) => state.checkForUpdates = checkForUpdates,
	setTimezones: (state: BaseState, timeZones: TimeZone[]) => state.timeZones = timeZones,
	setDisplayTimezone: (state: BaseState, timezone: TimeZone | null) => {
		(Settings as any).defaultZone = timezone ? timezone.iana : "system"
		state.displayTimezone = timezone
	},
	setTimeManagementSettings: (state: BaseState, settings: TimeManagementSettings) => state.timeManagementSettings = settings,
	setFileUploadOptions: (state: BaseState, fileUploadOptions: IntegrationUploadOptions[]) => state.fileUploadOptions = fileUploadOptions,
	setScheduleToastMessage: (state:BaseState, message:ScheduleMessage | null) => state.scheduleToastMessage = message,
	setUserInitComplete: (state: BaseState, userInitComplete: boolean) => state.userInitComplete = userInitComplete,
	setUserLoadError: (state: BaseState, error: ErrorInfo | null) => state.userLoadError = error,
	setApiError: (state: BaseState, error: ErrorInfo | null) => state.apiError = error
}

// #region Helper Functions

function getDataCacheByItemType (state: BaseState, itemType: CachedItemType): DataCache<any> | null {
	switch (itemType) {
		case CachedItemType.User:
			return state.userCache
		case CachedItemType.Field:
			return state.fieldCache
		case CachedItemType.Template:
			return state.templateCache
		default:
			return null
	}
}

function setDataCachePendingRefresh (state: BaseState, itemType: CachedItemType) {
	const cache = getDataCacheByItemType(state, itemType)
	if (!cache) {
		throw new Error("Data Cache not found: " + itemType)
	}
	console.log("Data Cache is stale and will update when needed:", itemType)
	cache.setPendingRefresh(true)
}

// #endregion

export default new Vuex.Store({
	state,
	getters,
	mutations,
	actions,
	modules: {
		cases,
		features,
		setup
	}
})
